import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';


interface Arc {
  min: number;
  max: number;
  color: string;
  disabledColor: string;

}

interface DisplayArc {
  arc: Arc;
  colour: string;
  pathData?: string;
  circles?: { x: number; y: number; radius: number }[];
}
@Component({
  selector: 'app-biometric-guage',
  templateUrl: './biometric-guage.component.html',
  styleUrls: ['./biometric-guage.component.scss'],
  standalone: false
})
export class BiometricGuageComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() value: number = 0;
  @Input() previousValue?: number | null;
  @Input() suffix?: string;
  @Input() prefix?: string;
  @Input() rangeText?: string | null;
  @Input() displayText?: string | null;
  @Input() noChangeText?: string | null;

  @Input() animated: boolean = true;
  @Input() animationDuration: number = 500;

  minValue: number = 0;
  maxValue: number = 100;

  arrowDirection?: 'up' | 'down';
  @Input() unitOfMeasure?: string | null;
  percentageChanged?: string | null;
  @Input() arcs: Arc[] = [];

  readonly outerRadius = 75; // Radius of the outer circle
  readonly circleRadius = 3; // Radius of each circle

  displayArc: DisplayArc[] = [];

  needlePosition: { x: number; y: number; plotValue: number } = { x: 100, y: 100, plotValue: 0 }; // Initial position

  displayedValue: number = 0;
  private animationFrameId: number | null = null;

  private startTime: number = 0;


  constructor() { }

  ngAfterViewInit(): void {
    // Ensure the component is fully initialized before starting animation
    this.renderControl();
    this.startAnimation();
  }


  ngOnInit(): void {
    if (this.value < this.minValue) this.value = this.minValue;
    if (this.value > this.maxValue) this.value = this.maxValue;
    this.renderControl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['arcs']) {
      this.renderControl();
    }
    this.startAnimation();
  }


  private startAnimation() {
    if (this.animated) {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }

      this.startTime = performance.now();
      this.animate();
    } else {
      this.displayedValue = this.value;
      this.updateNeedlePosition();
    }
  }

  private animate() {
    const currentTime = performance.now();
    const elapsedTime = currentTime - this.startTime;
    const progress = Math.min(elapsedTime / this.animationDuration, 1);

    // Calculate the interpolated value between minValue and maxValue
    this.displayedValue = this.minValue + (progress * (this.value - this.minValue));

    // Ensure the displayedValue is within the bounds of minValue and maxValue
    this.displayedValue = this.formatValue(Math.max(this.minValue, Math.min(this.displayedValue, this.maxValue)));

    this.updateNeedlePosition();

    if (progress < 1) {
      this.animationFrameId = requestAnimationFrame(() => this.animate());
    } else {
      this.displayedValue = this.value;  // Ensure it ends exactly at the target value
      this.updateNeedlePosition();

    }
  }

  private formatValue(value: number): number {
    if (!value) return value;
    const hasDecimal = value % 1 !== 0;
    const val = hasDecimal ? value.toFixed(1) : value.toString();
    return +val;
  }


  private renderControl(): void {
    this.displayArc = [];
    this.updateArrow();
    if (this.arcs && this.arcs.length > 0) {
      this.minValue = this.arcs[0].min;
      this.maxValue = this.arcs[this.arcs.length - 1].max;
    }
    this.arcs.forEach(arc => {
      const displayArc: DisplayArc = {
        arc: {
          min: arc.min,
          max: arc.max,
          color: arc.color,
          disabledColor: arc.disabledColor
        },
        colour: arc.color
      }
      this.displayArc.push(displayArc);

    });

    for (let x = 0; x < this.displayArc.length; x++) {
      if (this.value >= this.displayArc[x].arc.min && this.value < this.displayArc[x].arc.max) {
        this.displayArc[x].colour = this.displayArc[x].arc.color;
        if (x > 0) {
          this.displayArc[x - 1].colour = this.displayArc[x - 1].arc.disabledColor;
        }
      } else {
        this.displayArc[x].colour = this.displayArc[x].arc.disabledColor;
      }
    }

    this.displayArc.forEach(displayArc => {
      this.getArcCircles(displayArc);
    });


  }

  getRotation(value: number): number {
    return (value / 100) * 180;
  }

  getArcCircles(displayArc: DisplayArc) {

    if (!this.arcs || !this.arcs.length)
      return;
    const arc = displayArc.arc;

    const outerRadius = this.outerRadius;
    const centerX = 100;
    const centerY = 100;
    const gapDegrees = 6;

    // Normalize to 0–100% range
    let startAngle = this.getRotation(this.normalizeValue(arc.min));
    let endAngle = this.getRotation(this.normalizeValue(arc.max));

    const isFirst = arc.min === this.arcs[0].min && arc.max === this.arcs[0].max;
    const isLast = arc.min === this.arcs[this.arcs.length - 1].min && arc.max === this.arcs[this.arcs.length - 1].max;

    if (!isFirst) {
      startAngle += gapDegrees / 2;
    } else {
      startAngle = 0;
    }
    if (!isLast) {
      endAngle -= gapDegrees / 2;
    } else {
      endAngle = 180;
    }

    // 🔁 Adjust both angles so that 0% = 180° (i.e., 9 o'clock)
    const start = this.polarToCartesian(centerX, centerY, outerRadius, startAngle - 90);
    const end = this.polarToCartesian(centerX, centerY, outerRadius, endAngle - 90);

    const largeArcFlag = endAngle - startAngle > 180 ? '1' : '0';
    const sweepFlag = '1'; // clockwise

    const d = [
      `M ${start.x} ${start.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`
    ].join(' ');

    displayArc.pathData = d;
    displayArc.circles = [
      { x: start.x, y: start.y, radius: this.circleRadius },
      { x: end.x, y: end.y, radius: this.circleRadius }
    ];

    displayArc.circles = [];
  }

  polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  }

  updateNeedlePosition() {
    let needleValue = this.displayedValue;
    let boundaryStart = false;
    let boundaryEnd = false;

    const lastArc = this.arcs[this.arcs.length - 1];
    const minStepPercentage = ((this.maxValue - this.minValue) / 100);

    if (needleValue >= lastArc.max) {
      needleValue = lastArc.max - minStepPercentage; // pin needle slightly before max
    }

    if (this.arcs) {
      for (let x = 0; x < this.arcs.length; x++) {
        const arc = this.arcs[x];
        if (needleValue === arc.max) boundaryEnd = true;
        if (needleValue === arc.min) boundaryStart = true;
      }
    }

    if (boundaryStart) {
      needleValue += 2*minStepPercentage;
    } else if (boundaryEnd) {
      needleValue -= 2*minStepPercentage;
    }


    const anglePosition = this.normalizeValue(needleValue) / 100;
    const angle = this.getRotation(anglePosition);
    const needleOuter = this.polarToCartesian(97, 100, this.outerRadius * 1.15, angle);
    this.needlePosition = { x: needleOuter.x, y: needleOuter.y, plotValue: needleValue };

    for (let x = 0; x < this.displayArc.length; x++) {
      if (needleValue >= this.displayArc[x].arc.min && needleValue <= this.displayArc[x].arc.max) {
        this.displayArc[x].colour = this.displayArc[x].arc.color;
        if (x > 0) {
          this.displayArc[x - 1].colour = this.displayArc[x - 1].arc.disabledColor;
        }
      } else {
        this.displayArc[x].colour = this.displayArc[x].arc.disabledColor;
      }
    }
  }

  getNeedleTransform(): string {
    const rotation = this.getRotation(this.normalizeValue(this.needlePosition.plotValue)) - 90;
    return `rotate(${rotation}, 100, 100) translate(${this.needlePosition.x}, ${this.needlePosition.y}) scale(0.5)`;
  }

  normalizeValue(value: number): number {
    let result = ((value - this.minValue) / (this.maxValue - this.minValue)) * 100;
    if (result < 1)
      result = 1;
    if (result > 100)
      result = 100;
    return result;
  }

  private updateArrow() {
    this.arrowDirection = undefined;
    this.percentageChanged = undefined;

    if (this.previousValue && this.value) {

      let change = (this.value - this.previousValue) / this.previousValue * 100;
      if (change < 0) {
        change = change * -1;
        this.arrowDirection = 'down';
      } else if (change > 0) {
        this.arrowDirection = 'up';
      }
      this.percentageChanged = change.toFixed(0) + '%';
      if (this.percentageChanged == 'NaN%' || change == 0) {
        this.percentageChanged = undefined;
      }
    }
  }
}
